<!-- Generated by Copilot -->
<template>
  <v-card class="time-range-status-list">
    <v-card-title class="text-h6 d-flex align-center">
      Time Range Status
      <v-spacer></v-spacer>
      <v-chip-group v-model="statusFilter" mandatory>
        <v-chip value="all" size="small">All ({{ allStats.length }})</v-chip>
        <v-chip value="success" size="small" color="success">Success ({{ successCount }})</v-chip>
        <v-chip value="error" size="small" color="error">Failed ({{ errorCount }})</v-chip>
      </v-chip-group>
    </v-card-title>
    
    <v-card-text class="pa-0">
      <v-virtual-scroll
        :items="filteredStats"
        :height="height"
        item-height="80"
      >
        <template v-slot:default="{ item }">
          <v-card
            :class="['time-range-card ma-2', getStatusClass(item), { 'selected': item === props.selectedStat }]"
            variant="outlined"
            density="compact"
            @click="handleItemClick(item)"
            style="cursor: pointer;"
          >
            <v-card-text class="pa-2">
              <div class="d-flex align-center">
                <v-icon 
                  :icon="item.status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'"
                  :color="item.status === 'success' ? 'success' : 'error'"
                  class="mr-2"
                ></v-icon>
                <div class="flex-grow-1">
                  <div class="text-caption font-weight-bold">
                    {{ formatTimeRange(item.timeRange) }}
                  </div>
                  <div class="text-caption">
                    Duration: {{ item.duration.toFixed(0) }}ms | 
                    Samples: {{ item.sampleCount }}
                    <span v-if="item.retried" :class="getRetryClass(item)">
                      {{ getRetryLabel(item) }}
                    </span>
                  </div>
                  <div class="text-caption">
                    <a 
                      :href="getInspectUrl(item.url)" 
                      target="_blank" 
                      class="text-primary text-decoration-none"
                      @click.stop
                    >
                      <v-icon size="x-small" class="mr-1">mdi-open-in-new</v-icon>
                      Inspect Request
                    </a>
                  </div>
                  <div v-if="item.status === 'error'" class="text-caption text-error">
                    <strong>{{ item.errorType }}:</strong> {{ item.errorMessage || 'Unknown error' }}
                  </div>
                </div>
                <v-chip 
                  :color="getStatusColor(item)" 
                  size="x-small"
                  variant="flat"
                >
                  {{ item.status }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </template>
      </v-virtual-scroll>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { RequestStats } from '@/esri/services/TempoDataService';

interface Props {
  stats: RequestStats[];
  height?: number;
  selectedStat?: RequestStats | null;
}

const props = withDefaults(defineProps<Props>(), {
  height: 400,
  selectedStat: null
});

const emit = defineEmits<{
  (e: 'select', stat: RequestStats): void;
}>();

const statusFilter = ref<'all' | 'success' | 'error'>('all');

const allStats = computed(() => props.stats);

const successCount = computed(() => 
  props.stats.filter(s => s.status === 'success').length
);

const errorCount = computed(() => 
  props.stats.filter(s => s.status === 'error').length
);

const filteredStats = computed(() => {
  if (statusFilter.value === 'all') {
    return props.stats;
  }
  return props.stats.filter(s => s.status === statusFilter.value);
});

function formatTimeRange(range: { start: number; end: number }): string {
  const start = new Date(range.start);
  const end = new Date(range.end);
  
  const dateFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };
  
  return `${start.toLocaleDateString('en-US', dateFormat)} - ${end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
}

function getStatusClass(stat: RequestStats): string {
  return stat.status === 'success' ? 'status-success' : 'status-error';
}

function getStatusColor(stat: RequestStats): string {
  if (stat.status === 'success') return 'success';
  if (stat.errorType === '503') return 'warning';
  return 'error';
}

function getRetryLabel(stat: RequestStats): string {
  if (stat.retriedFrom503) {
    return stat.status === 'success' 
      ? '(Retried from 503 ✓)' 
      : '(Retried from 503 ✗)';
  }
  return stat.status === 'success'
    ? '(Retried ✓)'
    : '(Retried ✗)';
}

function getRetryClass(stat: RequestStats): string {
  if (stat.status === 'success') {
    return 'text-success font-weight-bold';
  }
  return 'text-error font-weight-bold';
}

function getInspectUrl(url: string): string {
  // Replace f=pjson with f=html to get the human-readable version
  return url.replace('f=pjson', 'f=html');
}

function handleItemClick(stat: RequestStats) {
  emit('select', stat);
}
</script>

<style scoped>
.time-range-card {
  transition: all 0.2s;
}

.time-range-card:hover {
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.status-success {
  border-left: 4px solid rgb(76, 175, 80);
}

.status-error {
  border-left: 4px solid rgb(244, 67, 54);
}

.time-range-card.selected {
  background-color: rgba(33, 150, 243, 0.1);
  border: 2px solid rgb(33, 150, 243);
}
</style>
